// src/lib/lifecycle-matrix.ts
//
// IR-1425 r10 — ONE vocabulary for the group lifecycle matrix, shared by the
// Groups tab, the Avatars expander, Monitoring and the group card so the three
// tabs cannot disagree about one group (Rotem, ROUNDS 4: "the three tabs agree
// for the same group").
//
// The backend (iris-backend `api/ops/services/group_lifecycle.py`, packages
// 1-3, origin/main 04d91f31) serves label and colour on every row and every
// breakdown item. The constants below are a FALLBACK ONLY — a served `label` /
// `color` always wins. They exist so an option list can be drawn before the
// first response and so a pre-port backend still gets a readable screen.
//
// Pure module: no React. The components live in
// `src/components/iris/lifecycle-matrix.tsx`.

/**
 * The 11 Lifecycle (LCP) values, matrix order = backend `LCP_ALL`.
 *
 * IR-1425 r12c (Rotem 2026-10-12): Sampled retired — a roster alone is
 * Suspended "No messages since membership was confirmed" or Needs Review
 * "awaiting the first message". A backend still serving `sampled` draws no
 * tile and no filter option (every list below iterates these keys).
 */
export const LCP_KEYS = [
  "waiting_approval",
  "approval_declined",
  "active",
  "suspended",
  "inactive",
  // IR-1769 r6 (Rotem 65049): the backend serves Stopped (LCP_STOPPED) between
  // Inactive and Needs Review; without the key it had no tile, no filter option
  // and fell out of the default `lcp=` set (every list here iterates the keys).
  "stopped",
  "needs_review",
  "not_relevant",
  "platform_deleted",
  "archived",
  "historic",
] as const;
export type LcpKey = (typeof LCP_KEYS)[number];

export const LCP_FALLBACK_LABELS: Record<LcpKey, string> = {
  waiting_approval: "Waiting for Approval",
  approval_declined: "Approval Declined",
  active: "Active",
  suspended: "Suspended",
  inactive: "Inactive",
  stopped: "Stopped",
  needs_review: "Needs Review",
  not_relevant: "Not Relevant",
  platform_deleted: "Platform Deleted / Removed",
  archived: "Archived",
  historic: "Historic",
};

export const LCP_FALLBACK_COLORS: Record<LcpKey, string> = {
  waiting_approval: "blue",
  approval_declined: "red",
  active: "green",
  suspended: "orange",
  inactive: "slate",
  // Same served name as Not Relevant. The Lifecycle strip is label-led tiles,
  // not a ring, so two greys never merge; the per-key hex override below is for
  // the Collection RING only (IR-1769 r6).
  stopped: "grey",
  needs_review: "amber",
  not_relevant: "grey",
  platform_deleted: "black",
  archived: "blue",
  historic: "stone",
};

/**
 * IR-1425 r12c: local labels for the two reason keys that replaced Sampled,
 * for a backend that serves the key with a null label. The served
 * `lcp_reason_label` ALWAYS wins — it may carry a date the fallback cannot
 * know ("Member since 2026-10-01, no messages yet"). Unknown keys get nothing.
 */
export const LCP_REASON_FALLBACK_LABELS: Record<string, string> = {
  no_message_since_membership: "No messages since membership was confirmed",
  awaiting_first_message: "Member confirmed, no messages yet",
};

/**
 * IR-1425 r12c: what the collector observed on its latest attempt, in the
 * brief's order. The VALUE drives colour and filtering; the served LABEL is
 * the text (it carries times and ranges) — never match on the label (G14).
 */
export const OBSERVATION_KEYS = [
  "messages",
  "could_not_check",
  "checked_no_messages",
  "roster_only",
  "not_checked",
  "data_read_but_discarded",
  "unknown",
  "no_message_observed",
] as const;
export type ObservationKey = (typeof OBSERVATION_KEYS)[number];
export type ObservationTone = "neutral" | "muted" | "warning";

/**
 * Tone of the observation line, keyed on the VALUE: a failed or discarded
 * read is a warning (the contradiction orange), a non-attempt is muted, and
 * everything else — including null and values this bundle predates — keeps
 * today's neutral so unchanged rows do not recolour.
 */
export function observationTone(value: string | null | undefined): ObservationTone {
  if (value === "could_not_check" || value === "data_read_but_discarded") return "warning";
  if (value === "not_checked" || value === "unknown") return "muted";
  return "neutral";
}

export function observationColor(tone: ObservationTone): string {
  if (tone === "warning") return "var(--iris-neon-orange)";
  if (tone === "muted") return "var(--iris-fg-5)";
  return "var(--iris-fg-4)";
}

/** The 8 Collection-axis states, matrix order = backend `CAXIS_ALL`. */
export const CAXIS_KEYS = [
  "collection_pending",
  "collecting",
  "paused",
  "sampling",
  "stop_pending",
  "stopped",
  "historic_import",
  "not_checked",
] as const;
export type CaxisKey = (typeof CAXIS_KEYS)[number];

export const CAXIS_FALLBACK_LABELS: Record<CaxisKey, string> = {
  collection_pending: "Collection Pending",
  collecting: "Collecting",
  paused: "Paused",
  sampling: "Sampling",
  stop_pending: "Stop Pending",
  stopped: "Stopped",
  historic_import: "Historic Import / Not Live Collection",
  not_checked: "Not Checked",
};

export const CAXIS_FALLBACK_COLORS: Record<CaxisKey, string> = {
  collection_pending: "blue",
  collecting: "green",
  paused: "slate",
  sampling: "yellow",
  stop_pending: "orange",
  stopped: "grey",
  historic_import: "stone",
  not_checked: "grey",
};

/**
 * The 9 per-row Lifecycle keys (`LCP_ROW_KEYS`). Every one optional AND
 * nullable: absent on a pre-port backend, all `null` for an internally
 * deleted group (`lcp === null`). Callers keep today's rendering in both cases.
 */
export interface LcpFields {
  lcp?: string | null | undefined;
  lcp_label?: string | null | undefined;
  lcp_color?: string | null | undefined;
  lcp_reason?: string | null | undefined;
  lcp_reason_label?: string | null | undefined;
  lcp_source?: "manual" | "automatic" | string | null | undefined;
  lcp_set_at?: string | null | undefined;
  lcp_quiet?: boolean | null | undefined;
  lcp_avatar_note?: string | null | undefined;
}

/** The 10 per-row Collection-axis keys (`COLLECTION_AXIS_ROW_KEYS`). */
export interface CollectionAxisFields {
  collection_axis?: string | null | undefined;
  collection_axis_label?: string | null | undefined;
  collection_axis_color?: string | null | undefined;
  collection_observation?: ObservationKey | string | null | undefined;
  collection_observation_label?: string | null | undefined;
  collection_next_sample_at?: string | null | undefined;
  collection_stop_reason?: string | null | undefined;
  collection_stop_reason_label?: string | null | undefined;
  collection_contradiction?: boolean | null | undefined;
  collection_contradiction_label?: string | null | undefined;
}

/** One item of `breakdown.by_lcp` / `by_collection_axis` / `groups_by_lcp`. */
export interface LcpBreakdownItem {
  key: string;
  label: string;
  color: string;
  count: number;
}

// ---------------------------------------------------------------------------
// Colours — keyed on the backend's colour NAME (`STATUS_COLORS`), never on a
// label (G14: a relabel must not recolour anything).

/**
 * CSS value for a status dot. Moved here from `group-status-cell.tsx`
 * (IR-1228 P5b) so the legacy cell and the matrix chip share one table.
 * `grey` IS the neutral token — that is its meaning (Not Relevant, Stopped,
 * Not Checked), not a fallback. Unknown names fall back to the same neutral.
 */
const DOT_COLOR_BY_NAME: Record<string, string> = {
  green: "var(--iris-neon-green)",
  yellow: "var(--iris-neon-yellow)",
  orange: "var(--iris-neon-orange)",
  amber: "#f0a92b",
  red: "var(--iris-neon-red)",
  purple: "var(--iris-neon-purple)",
  blue: "var(--iris-neon-cyan)",
  slate: "#7d8590",
  // IR-1228 P5: Historic — without `stone` every Historic row fell back to the
  // SAME dot as `grey`, which already means Deleted / Not Relevant.
  stone: "#8b8378",
  grey: "var(--iris-fg-4)",
  black: "#484f58",
};

export function lcpDotColor(name: string | null | undefined): string {
  return DOT_COLOR_BY_NAME[name ?? ""] ?? "var(--iris-fg-4)";
}

/**
 * Hex for an SVG slice (fill attributes do not resolve `var()` reliably —
 * same reason `INGESTION_SLICE_COLORS` is written out). Values ARE the iris
 * tokens, named beside each one.
 *
 * `blue` is `--iris-neon-blue` here, not the cyan the dot uses: as a slice
 * beside green, cyan and green sit too close (the IR-1482 r2 finding for
 * yellow applies). Checked 2026-10-06 as adjacent slices on `--iris-bg-2`
 * #0b0d14: every neighbouring pair of the 8 Collection states clears ΔE2000
 * ≥ 24 normal-vision and ≥ 19 protan once the two greys are split by key
 * (see `LCP_SLICE_HEX_BY_KEY_OVERRIDE`).
 */
const NEUTRAL_SLICE_HEX = "#6b7184"; // --iris-fg-4 // token-exempt
const SLICE_HEX_BY_NAME: Record<string, string> = {
  green: "#39ff88", // --iris-neon-green // token-exempt
  yellow: "#eaff2a", // --iris-neon-yellow // token-exempt
  orange: "#ff9933", // --iris-neon-orange // token-exempt
  amber: "#f0a92b", // token-exempt
  red: "#ff3355", // --iris-neon-red // token-exempt
  purple: "#a855f7", // --iris-neon-purple // token-exempt
  blue: "#3e8dff", // --iris-neon-blue // token-exempt
  slate: "#7d8590", // token-exempt
  stone: "#8b8378", // token-exempt
  grey: "#6b7184", // --iris-fg-4 // token-exempt
  black: "#484f58", // token-exempt
};

export function lcpSliceHex(name: string | null | undefined): string {
  return SLICE_HEX_BY_NAME[name ?? ""] ?? NEUTRAL_SLICE_HEX;
}

/**
 * Per-KEY override for the one case a colour NAME cannot separate: the
 * Collection ring has two `grey` states (`stopped`, `not_checked`) and they
 * would be one indistinguishable wedge. Keyed on the matrix KEY — never the
 * label — so a relabel does not recolour. Both stay "greys" (neutral is the
 * meaning); they differ in lightness only. Palette check 2026-10-06:
 * stopped|historic_import 24.3 / 24.8, historic_import|not_checked 25.5 / 25.0
 * (normal / protan ΔE2000), both PASS.
 */
export const LCP_SLICE_HEX_BY_KEY_OVERRIDE: Record<string, string> = {
  stopped: "#c9cdd8", // light neutral // token-exempt
  not_checked: "#4a5064", // dark neutral // token-exempt
};

/** Slice hex for a breakdown item: key override first, then its colour name. */
export function lcpSliceHexForItem(item: {
  key: string;
  color: string | null | undefined;
}): string {
  return LCP_SLICE_HEX_BY_KEY_OVERRIDE[item.key] ?? lcpSliceHex(item.color);
}

// ---------------------------------------------------------------------------
// The reason line

export function lcpSourceLabel(source: string | null | undefined): string | null {
  if (source === "manual") return "set by hand";
  if (source === "automatic") return "automatic";
  return null;
}

/** States whose reason line carries WHO set it and WHEN (matrix: "with reason, source and time"). */
export const SHOW_SOURCE_FOR: ReadonlySet<string> = new Set([
  "needs_review",
  "suspended",
  "inactive",
]);

/**
 * Date in the tab's existing short style ("Oct 5") — the Groups tab's
 * `fmtDate` — so one row does not mix two date formats (decision 12). The
 * full ISO string goes in `title` at the call site.
 */
export function formatLcpDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * The muted second line under a lifecycle chip, or null when there is nothing
 * to say. Reason first; for Needs Review / Suspended / Inactive the source and
 * the date follow — those are the states Rotem asked to see "with reason,
 * source and time". Other states show the reason alone.
 */
export function lifecycleReasonText(fields: LcpFields): string | null {
  const parts: string[] = [];
  // IR-1425 r12c: the served label wins; a null label with a known reason key
  // falls back to the local copy; an unknown key with no label says nothing.
  const reason =
    fields.lcp_reason_label ??
    (fields.lcp_reason ? LCP_REASON_FALLBACK_LABELS[fields.lcp_reason] : undefined);
  if (reason) parts.push(reason);
  if (fields.lcp && SHOW_SOURCE_FOR.has(fields.lcp)) {
    const src = lcpSourceLabel(fields.lcp_source);
    if (src) parts.push(src);
    if (fields.lcp_set_at) parts.push(formatLcpDate(fields.lcp_set_at));
  }
  return parts.length ? parts.join(" · ") : null;
}

/** "last 24 h" / "last 7 d" — every count prints its window. */
export function windowCaption(hours: number): string {
  if (hours === 24) return "last 24 h";
  if (hours > 24 && hours % 24 === 0) return `last ${hours / 24} d`;
  return `last ${hours} h`;
}

// ---------------------------------------------------------------------------
// Filters and breakdowns

export interface FilterOption {
  value: string;
  label: string;
}

/** Lifecycle filter options, matrix order; served labels win over the local copy. */
export function lcpOptions(served?: LcpBreakdownItem[] | null): FilterOption[] {
  const byKey = new Map((served ?? []).map((i) => [i.key, i.label]));
  return LCP_KEYS.map((k) => ({ value: k, label: byKey.get(k) ?? LCP_FALLBACK_LABELS[k] }));
}

/** Collection filter options, matrix order; served labels win over the local copy. */
export function caxisOptions(served?: LcpBreakdownItem[] | null): FilterOption[] {
  const byKey = new Map((served ?? []).map((i) => [i.key, i.label]));
  return CAXIS_KEYS.map((k) => ({ value: k, label: byKey.get(k) ?? CAXIS_FALLBACK_LABELS[k] }));
}

/**
 * Default Lifecycle selection: every value but Historic. An active `lcp=`
 * lifts the backend's Historic hide, but Historic is not in the set so it
 * stays hidden; a deleted group never matches `lcp=`, so Deleted stays hidden
 * (the IR-1431 default, preserved).
 */
export const DEFAULT_LCP_FILTER: string[] = LCP_KEYS.filter((k) => k !== "historic");

/** Client-side tally by `lcp` — for tests asserting strip == rows; not a render source. */
export function tallyLcp(rows: ReadonlyArray<{ lcp?: string | null }>): Record<LcpKey, number> {
  const out = Object.fromEntries(LCP_KEYS.map((k) => [k, 0])) as Record<LcpKey, number>;
  for (const r of rows) {
    if (r.lcp && (LCP_KEYS as readonly string[]).includes(r.lcp)) out[r.lcp as LcpKey] += 1;
  }
  return out;
}

export interface BreakdownEntry {
  key: LcpKey;
  label: string;
  count: number;
}

/**
 * `lcp_breakdown` (key → count over the 10 keys) as an ordered list with the
 * zero entries omitted. null/undefined in → null out (the caller renders "—").
 */
export function breakdownEntries(
  counts: Record<string, number> | null | undefined,
): BreakdownEntry[] | null {
  if (counts == null) return null;
  return LCP_KEYS.filter((k) => (counts[k] ?? 0) > 0).map((k) => ({
    key: k,
    label: LCP_FALLBACK_LABELS[k],
    count: counts[k] ?? 0,
  }));
}

// ---------------------------------------------------------------------------
// IR-1769 step 3a -- the avatar stop, shown on a group as its OWN line.
//
// DISPLAY ONLY. The group's Lifecycle (Suspended / Inactive / ...) and every
// counter stay exactly as served; this never produces a lifecycle word, a group
// Collection word or a count (that is step 3b, gated on Rotem). A value the
// backend does not hold reads "Unknown" -- never blank, never zero, never
// invented (Ingestion Matrix law: Unknown is not zero).

export interface AvatarStopEntry {
  avatar_id?: number | null;
  name?: string | null;
  stopped_by?: string | null;
  stop_reason?: string | null;
  requested_at?: string | null;
  confirmed_at?: string | null;
  collection_state?: "stop_pending" | "stopped" | "stop_failed" | string | null;
  contradicted?: boolean | null;
}

export interface AvatarStopField {
  summary?: string | null;
  has_active_alternate?: boolean | null;
  earliest_requested_at?: string | null;
  avatars?: AvatarStopEntry[] | null;
}

export const AVATAR_STOP_UNKNOWN = "Unknown";

const AVATAR_STOP_STATE_LABELS: Record<string, string> = {
  stop_pending: "Stop pending",
  stopped: "Stopped",
  stop_failed: "Stop failed",
};

/** Exactly the words the avatar Collection column already uses (IR-1769 r5 3.1). */
export const AVATAR_STOP_CONTRADICTION_LABEL =
  "Stop failed: still collected after the stop was required";

/** `2026-10-21 10:00 UTC`, or "Unknown" when the backend holds no such time. */
export function formatStopTime(iso: string | null | undefined): string {
  if (!iso) return AVATAR_STOP_UNKNOWN;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return AVATAR_STOP_UNKNOWN;
  return `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

function orUnknown(v: string | null | undefined): string {
  const t = typeof v === "string" ? v.trim() : "";
  return t === "" ? AVATAR_STOP_UNKNOWN : t;
}

export interface AvatarStopLine {
  key: string;
  /** One-line text for the cell: avatar, state, initiator, reason, times. */
  text: string;
  state: string;
  contradicted: boolean;
}

/** One line per Stopped covering avatar; [] when the group has none (or the field is absent). */
export function avatarStopLines(stop: AvatarStopField | null | undefined): AvatarStopLine[] {
  const avatars = stop?.avatars;
  if (!Array.isArray(avatars)) return [];
  return avatars.map((a, i) => {
    const state = typeof a.collection_state === "string" ? a.collection_state : "";
    const stateLabel = AVATAR_STOP_STATE_LABELS[state] ?? AVATAR_STOP_UNKNOWN;
    const contradicted = a.contradicted === true;
    const parts = [
      `Avatar ${orUnknown(a.name)}: ${stateLabel}`,
      `by ${orUnknown(a.stopped_by)}`,
      `reason: ${orUnknown(a.stop_reason)}`,
      `requested ${formatStopTime(a.requested_at)}`,
      `confirmed ${formatStopTime(a.confirmed_at)}`,
    ];
    if (contradicted) parts.push(AVATAR_STOP_CONTRADICTION_LABEL);
    return {
      key: String(a.avatar_id ?? `${a.name ?? "avatar"}-${i}`),
      text: parts.join(" · "),
      state,
      contradicted,
    };
  });
}
